import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    SimpleChanges,
    ChangeDetectorRef,
    forwardRef,
} from '@angular/core';
import { User, UserGroup } from '@intranet/modules/users/user.types';
import { UserOrGroup } from '..';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'user-select',
    templateUrl: './user-select.component.html',
    styleUrls: ['./user-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => UserSelectComponent),
        multi: true
    }]
})
export class UserSelectComponent implements OnInit, ControlValueAccessor  {
    @Input() users: User[];
    @Input() userIds: string[] | null = null;
    @Input() groupIds: number[] | null = null;
    @Input() singleSelect: boolean = false;
    @Input() canSelectAll: boolean = false;
    @Input() disableGroups: boolean = false;
    @Input() visible: boolean = false;
    @Input() avatarShowLabel = false;
    @Input() avatarLabelPosition: 'right'|'bottom' = 'right';
    // Opt-in only — off by default, so the other consumers of this shared
    // component (holidays filter/calendar, action manage/bulk dialogs) are
    // unaffected unless they explicitly turn this on.
    @Input() showAvailability = false;

    @Output() clicked = new EventEmitter<UserOrGroup>();
    @Output() selected = new EventEmitter<{
        userIds: string[];
        groupIds: number[];
    }>();
    @Output() selectionChanged = new EventEmitter<Array<{ id: string, name: string }>>();

    protected userGroups: UserGroup[];
    private _selectedUserIds: Set<string>;
    private _selectedGroupIds: Set<number>;
    private _onChange: (v: { userIds: string[]; groupIds: number[] }) => void = () => {};
    private _onTouched: () => void = () => {};

    constructor(
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this._selectedUserIds = new Set<string>(this.userIds ?? []);
        this._selectedGroupIds = new Set<number>(this.groupIds ?? []);

        if (this.users) {
            this.users
                .map((u) => u.userGroup)
                .filter((v, i, a) => a.indexOf(v) === i)
                .sort((a, b) => a.groupName < b.groupName ? -1 : 1);

            let grouped = this.users.sort((a, b) => {
                if (a.userGroup.groupName < b.userGroup.groupName)
                    return -1;
                if (a.userGroup.groupName === b.userGroup.groupName) {
                    if (a.shortName < b.shortName) return -1;
                    if (a.shortName === b.shortName) return 0;
                    return 1;
                }
                return 1;
            });

            this.userGroups = [];
            var ug: UserGroup | null = null;

            for (var i = 0; i < grouped.length; i++) {
                if (
                    ug === null ||
                    ug.groupName !== grouped[i].userGroup.groupName
                ) {
                    ug = {
                        groupName: grouped[i].userGroup.groupName,
                        userGroupId: grouped[i].userGroup.userGroupId,
                        users: [],
                    };
                    this.userGroups.push(ug);
                }
                ug.users.push(grouped[i]);
            }
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        // If userIds or groupIds change, update the selected sets
        if (changes.userIds  && !changes.userIds.firstChange) {
            this._selectedUserIds = new Set(this.userIds ?? []);
        }
        if (changes.groupIds && !changes.groupIds.firstChange) {
            this._selectedGroupIds = new Set(this.groupIds ?? []);
        }

        this.cdr.markForCheck();
    }

    formatAvatarUsers(users: User[]): Array<{ id: string; name: string }> {
      return users.map(u => ({
        id:   u.id,
        name: `${u.firstName} ${u.surname}`
      }));
    }

    isUserSelected(user: User) {
        return this._selectedUserIds?.has(user.id) ?? false;
    }

    onClose() {
        this.visible = false; // Close the modal
    }

    isGroupSelected(group: UserGroup) {
        return this._selectedGroupIds?.has(group.userGroupId) ?? false;
    }

    onGroupClick(group: UserGroup) {
        const value = <UserOrGroup>{ isGroup: true, group }
        this.clicked.emit(value);

        if (this.singleSelect) {
            const id = group.userGroupId;
            if (this._selectedGroupIds.has(id)) {
                this._selectedGroupIds.clear();
            } else {
                this._selectedGroupIds.clear();
                this._selectedUserIds.clear();
                this._selectedGroupIds.add(id);
            }
            this._emitSelected();
        } else {
            const id = group.userGroupId;
            if (this._selectedGroupIds.has(id)) {
                this._selectedGroupIds.delete(id);
            } else {
                this._selectedGroupIds.add(id);
            }
            this._emitSelected();
        }
    }

  // Read the value from the model and update the component.
  writeValue(value: { userIds: string[]; groupIds: number[] } | null): void {
      if (!value) {
          this._selectedUserIds.clear();
          this._selectedGroupIds.clear();
      } else {
          this._selectedUserIds = new Set(value.userIds);
          this._selectedGroupIds = new Set(value.groupIds);
      }
      this.cdr.markForCheck();
  }

    // Save the callback to call when the value changes.
    registerOnChange(
        fn: (value: { userIds: string[]; groupIds: number[] }) => void
    ): void {
        this._onChange = fn;
    }

    // Save the callback to call when the component is touched.
    registerOnTouched(fn: () => void): void {
        this._onTouched = fn;
    }

    // Called by Angular when the component is disabled or enabled.
    setDisabledState?(isDisabled: boolean): void {
        this.disableGroups = isDisabled;
        this.cdr.markForCheck();
    }

    onGroupSelectAll(group: UserGroup, event: any) {
        if (event.target.checked) {
            group.users.forEach((u) => {
                if (!this._selectedUserIds.has(u.id)) {
                    this._selectedUserIds.add(u.id);
                }
            });

            if (!this._selectedGroupIds.has(group.userGroupId)) {
                this._selectedGroupIds.add(group.userGroupId);
            }
        } else {
            group.users.forEach((u) => {
                this._selectedUserIds.delete(u.id);
            });
            this._selectedGroupIds.delete(group.userGroupId);
        }
        this._emitSelected();
    }

    isGroupFullySelected(group: UserGroup) {
        return this.isGroupSelected(group) && group.users.every((u) => this.isUserSelected(u));
    }

    isEverythingSelected() {
        return !!this.userGroups?.length && this.userGroups.every((g) => this.isGroupFullySelected(g));
    }

    onSelectEverything(event: any) {
        if (event.target.checked) {
            this.userGroups.forEach((g) => {
                this._selectedGroupIds.add(g.userGroupId);
                g.users.forEach((u) => this._selectedUserIds.add(u.id));
            });
        } else {
            this._selectedGroupIds.clear();
            this._selectedUserIds.clear();
        }
        this._emitSelected();
    }

    onUserClick(user: User) {
        const value = <UserOrGroup>{ isUser: true, user }
        this.clicked.emit(value);

        if (this.singleSelect) {
            const id = user.id;
            if (this._selectedUserIds.has(id)) {
                this._selectedUserIds.clear();
            } else {
                this._selectedGroupIds.clear();
                this._selectedUserIds.clear();
                this._selectedUserIds.add(id);
            }
            this._emitSelected();
        } else {

            const id = user.id;
            if (this._selectedUserIds.has(id)) {
                this._selectedUserIds.delete(id);
            } else {
                this._selectedUserIds.add(id);
            }
            this._emitSelected();
        }
    }

    private _emitSelected() {
        const userIds = Array.from(this._selectedUserIds);
        const groupIds = Array.from(this._selectedGroupIds);

        // notify Angular forms
        this._onChange({ userIds, groupIds });
        this._onTouched();

        // emit for external listeners
        const avatars = userIds.map(id => {
            const u = this.users.find(u => u.id === id)!;
            return { id, name: `${u.firstName} ${u.surname}` };
        });
        for (const gid of groupIds) {
            const g = this.userGroups.find(g => g.userGroupId === gid)!;
            avatars.push({ id: `group:${gid}`, name: g.groupName });
        }
        this.selectionChanged.emit(avatars);
        this.selected.emit({ userIds, groupIds });
  }
}
